import { QueryClient } from "@tanstack/react-query";

/**
 * Updates React Query cache for a specific module and item.
 *
 * @param queryClient - The React Query client instance
 * @param moduleKey - The module key for cache queries
 * @param itemId - The item ID being updated
 * @param responseData - The response data from the API
 * @param method - The HTTP method used for the request
 */
export function updateCacheForModule(
  queryClient: QueryClient,
  moduleKey: string,
  itemId: string,
  responseData: Record<string, unknown>,
  method: string,
): void {
  console.log(
    `🗂️ Updating cache for module: ${moduleKey}, itemId: ${itemId}, method: ${method}, hasResponseData: ${!!responseData}`,
  );

  // Update the specific item in cache immediately
  queryClient.setQueryData([moduleKey, itemId], responseData);

  // Also update the item in any list queries
  queryClient.setQueryData([moduleKey], (oldData: unknown) => {
    if (oldData && typeof oldData === "object" && "pages" in oldData) {
      // Handle infinite query
      const infiniteData = oldData as {
        pages: { items: Record<string, unknown>[] }[];
      };
      return {
        ...infiniteData,
        pages: infiniteData.pages.map((page) => ({
          ...page,
          items: page.items.map((item) =>
            item.id?.toString() === itemId
              ? { ...item, ...responseData }
              : item,
          ),
        })),
      };
    } else if (oldData && typeof oldData === "object" && "items" in oldData) {
      // Handle regular list query
      const listData = oldData as { items: Record<string, unknown>[] };
      return {
        ...listData,
        items: listData.items.map((item) =>
          item.id?.toString() === itemId ? { ...item, ...responseData } : item,
        ),
      };
    }
    return oldData;
  });

  // Also invalidate any queries that might have this module as part of their key
  queryClient.invalidateQueries({
    predicate: (query) => {
      const queryKey = query.queryKey;
      return (
        Array.isArray(queryKey) &&
        queryKey.includes(moduleKey) &&
        queryKey.length > 1
      );
    },
  });
}
